import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from "@mui/material";
import * as Yup from "yup";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useFormik } from "formik";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import { LARGE_FORM_MODAL_SX, DENSE_FORM_BODY_SX, DENSE_FIELD_PROPS } from "../../utils/formModalStyles";
import { createAdmission, getAdmissionById } from "../../services/EmergencyAdmissionService";
import { createEmergency } from "../../services/EmergencyDetailsService";

// const validationSchema = Yup.object({
//   patientName: Yup.string().required("Patient name is required"),
//   patientContact: Yup.string()
//   .matches(/^\d{10}$/, "Patient contact must be exactly 10 digits")
//   .required("Patient contact is required"),
//   attendantName: Yup.string()
//   .matches(/^[A-Za-z\s]+$/, "Attendant name can only contain letters and spaces")
//   .max(50, "Attendant name cannot be longer than 50 characters")
//   .required("Attendant name is required"),
//   attendantContact: Yup.string()
//   .matches(/^\d{10}$/, "Attendant contact must be exactly 10 digits")
//   .required("Patient contact is required"),
//   patientState: Yup.string().required("Patient state is required"),
//   createdAt: Yup.date().required("Admission date is required").nullable(),
//   admissionReason: Yup.string().required('Admission Reason is required'),
//   broughtBy: Yup.string().required('Brought By is required'),
//   broughtType: Yup.string().required('How patient was brought is required'),
//   firReport: Yup.string().required('Police Report is required'),
// });

const validationSchema = Yup.object({
  patientName: Yup.string().required("Patient name is required"),
  patientContact: Yup.string()
    .matches(/^\d{10}$/, "Patient contact must be exactly 10 digits")
    .required("Patient contact is required")
    .test('patient-not-equal-attendant', 'Patient and Attendant contact must be different', function (value) {
      const { attendantContact } = this.parent || {};
      if (!value || !attendantContact) return true;
      return String(value).trim() !== String(attendantContact).trim();
    }),
  attendantName: Yup.string()
    .matches(/^[A-Za-z\s]+$/, "Attendant name can only contain letters and spaces")
    .max(50, "Attendant name cannot be longer than 50 characters")
    .required("Attendant name is required"),
  attendantContact: Yup.string()
    .matches(/^\d{10}$/, "Attendant contact must be exactly 10 digits")
    .required("Attendant contact is required")
    .test('attendant-not-equal-patient', 'Patient and Attendant contact must be different', function (value) {
      const { patientContact } = this.parent || {};
      if (!value || !patientContact) return true;
      return String(value).trim() !== String(patientContact).trim();
    }),
  patientState: Yup.string().required("Patient state is required"),
  createdAt: Yup.date().required("Admission date is required").nullable(),
  admissionReason: Yup.string().required("Admission Reason is required"),
  broughtBy: Yup.string().required("Brought By is required"),
  broughtType: Yup.string().required("How patient was brought is required"),
  firReport: Yup.string().required("Police Report is required"),

  // ✅ Fix: Use function in `then` and `otherwise`
  ambulanceNumber: Yup.string().when('broughtType', {
    is: 'ambulance',
    then: () => Yup.string().required('Ambulance number is required'),
    otherwise: () => Yup.string().notRequired(),
  }),
  driverName: Yup.string().when('broughtType', {
    is: 'ambulance',
    then: () => Yup.string().required('Driver name is required'),
    otherwise: () => Yup.string().notRequired(),
  }),
  firFile: Yup.mixed().when('firReport', {
    is: 'yes',
    then: () => Yup.mixed().required('FIR file is required'),
    otherwise: () => Yup.mixed().notRequired(),
  }),
});

const EmergencyAdmission = ({ open, onClose, EmergencyId, refreshList }) => {
  const [ emergencyAdmissionInfo, setEmergencyAdmissionInfo] = useState([]);

  const formik = useFormik({
    initialValues: {
      patientName: "",
      patientContact: "",
      attendantName: "",
      attendantContact: "",
      patientState: "",
      status: "",
      createdAt: dayjs(),
      admissionReason: "",
      broughtBy: "",
      broughtType: "",
      firReport: "",
      firFile: null,
      ambulanceNumber: "",
      driverName: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        // Payload for admission
        const admissionPayload = {
          patientId: "0",
          patientName: values.patientName,
          patientContact: values.patientContact,
          attendantName: values.attendantName,
          attendantContact: values.attendantContact,
          patientState: values.patientState,
          status: '',
          createdAt: dayjs(values.createdAt).format("YYYY-MM-DDTHH:mm:ss"),
        };
        const admission = await createAdmission(admissionPayload);

        // Payload for emergency details
        const emergencyDetailsPayload = {
          emergencyId: admission.id, 
          admissionReason: values.admissionReason,
          broughtBy: values.broughtBy,
          broughtType: values.broughtType,
          firReport: values.firReport,
          firFile: values.firFile,
          ambulanceNumber: values.ambulanceNumber,
          driverName: values.driverName,
        };
        await createEmergency(emergencyDetailsPayload); 
        resetForm();
        if (refreshList) refreshList();
        onClose();
        alert("Emergency Admission created successfully");

      } catch (error) {
        console.error(error);
        alert("Failed to submit emergency admission.");
      }
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      sx={LARGE_FORM_MODAL_SX}
    >
      {/* Title */}
      <Typography
        variant="h5"
        sx={{ fontSize: "1.5rem", fontWeight: "bold", textAlign: "center", mb: 2 }}
      >
        Emergency Admission
      </Typography>

      {/* Form body — shell (BasicModal) owns scroll when needed */}
      <Box sx={{ pr: 0.5, ...DENSE_FORM_BODY_SX }}>
        <form onSubmit={formik.handleSubmit} noValidate>
          <Grid container spacing={1}>
            {/* Patient & Attendant Info */}
            <Grid item xs={6}>
              <TextField
                label="Patient Name"
                fullWidth
                name="patientName"
                value={formik.values.patientName}
                onChange={(e) => {
                  let inputValue = e.target.value;
                  
                  // Capitalize first letter
                  if (inputValue.length > 0) {
                    inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                  }
                  
                  formik.setFieldValue("patientName", inputValue);
                }}
                error={formik.touched.patientName && Boolean(formik.errors.patientName)}
                helperText={formik.touched.patientName && formik.errors.patientName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Patient Contact"
                fullWidth
                name="patientContact"
                placeholder="10 digit phone number"
                value={formik.values.patientContact}
                onChange={(e) => {
                  // Only allow digits and limit to 10 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  formik.setFieldValue('patientContact', value);
                }}
                inputProps={{ maxLength: 10 }}
                error={formik.touched.patientContact && Boolean(formik.errors.patientContact)}
                helperText={
                  formik.touched.patientContact && formik.errors.patientContact
                    ? formik.errors.patientContact
                    : "Enter 10 digit phone number"
                }
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Attendant Name"
                fullWidth
                name="attendantName"
                value={formik.values.attendantName}
                onChange={(e) => {
                  let inputValue = e.target.value;
                  
                  // Capitalize first letter
                  if (inputValue.length > 0) {
                    inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                  }
                  
                  formik.setFieldValue("attendantName", inputValue);
                }}
                error={formik.touched.attendantName && Boolean(formik.errors.attendantName)}
                helperText={formik.touched.attendantName && formik.errors.attendantName}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Attendant Contact"
                fullWidth
                name="attendantContact"
                placeholder="10 digit phone number"
                value={formik.values.attendantContact}
                onChange={(e) => {
                  // Only allow digits and limit to 10 digits
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  formik.setFieldValue('attendantContact', value);
                }}
                inputProps={{ maxLength: 10 }}
                error={formik.touched.attendantContact && Boolean(formik.errors.attendantContact)}
                helperText={
                  formik.touched.attendantContact && formik.errors.attendantContact
                    ? formik.errors.attendantContact
                    : "Enter 10 digit phone number"
                }
              />
            </Grid>

            {/* Patient State */}
            <Grid item xs={6}>
              <TextField
                select
                label="Patient State"
                fullWidth
                name="patientState"
                value={formik.values.patientState}
                onChange={formik.handleChange}
                error={formik.submitCount > 0 && Boolean(formik.errors.patientState)}
                helperText={formik.submitCount > 0 && formik.errors.patientState}
              >
                <MenuItem value="Critical">
                  <Box
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "yellow",
                      display: "inline-block",
                      mr: 1,
                    }}
                  />
                  Critical
                </MenuItem>
                <MenuItem value="Serious">
                  <Box
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "red",
                      display: "inline-block",
                      mr: 1,
                    }}
                  />
                  Serious
                </MenuItem>
                <MenuItem value="Stable">
                  <Box
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: "green",
                      display: "inline-block",
                      mr: 1,
                    }}
                  />
                  Stable
                </MenuItem>
              </TextField>
            </Grid>

            {/* Admission Date */}
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Admission Date"
                  value={formik.values.createdAt}
                  onChange={(val) => formik.setFieldValue("createdAt", val)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(formik.errors.createdAt) && formik.submitCount > 0,
                      helperText: formik.submitCount > 0 && formik.errors.createdAt,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {/* Emergency Info */}
            {/* <Grid item xs={6}>
              <TextField
                label="Admission Reason"
                fullWidth
                name="admissionReason"
                value={formik.values.admissionReason}
                onChange={formik.handleChange}
                error={formik.touched.admissionReason && Boolean(formik.errors.admissionReason)}
                helperText={formik.touched.admissionReason && formik.errors.admissionReason}
              />
            </Grid> */}
            <Grid item xs={6}>
              <FormControl fullWidth error={formik.touched.admissionReason && Boolean(formik.errors.admissionReason)}>
                <InputLabel>Admission Reason</InputLabel>
                <Select
                  label="Admission Reason"
                  name="admissionReason"
                  value={formik.values.admissionReason}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="accident">Accident</MenuItem>
                  <MenuItem value="health_issue">Health Issue</MenuItem>
                  <MenuItem value="referred">Referred</MenuItem>
                </Select>
                {formik.touched.admissionReason && formik.errors.admissionReason && (
                  <Typography variant="caption" color="error">
                    {formik.errors.admissionReason}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            {/* <Grid item xs={6}>
              <TextField
                label="Brought By"
                fullWidth
                name="broughtBy"
                value={formik.values.broughtBy}
                onChange={formik.handleChange}
                error={formik.touched.broughtBy && Boolean(formik.errors.broughtBy)}
                helperText={formik.touched.broughtBy && formik.errors.broughtBy}
              />
            </Grid> */}
            <Grid item xs={6}>
              <FormControl
                fullWidth
                error={formik.touched.broughtBy && Boolean(formik.errors.broughtBy)}
              >
                <InputLabel>Brought By</InputLabel>
                <Select
                  label="Brought By"
                  name="broughtBy"
                  value={formik.values.broughtBy}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="relative">Relative</MenuItem>
                  <MenuItem value="friend">Friend</MenuItem>
                  <MenuItem value="unknown">Unknown</MenuItem>
                </Select>
                {formik.touched.broughtBy && formik.errors.broughtBy && (
                  <Typography variant="caption" color="error">
                    {formik.errors.broughtBy}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label="How Patient Was Brought"
                fullWidth
                name="broughtType"
                value={formik.values.broughtType}
                onChange={formik.handleChange}
                error={formik.touched.broughtType && Boolean(formik.errors.broughtType)}
                helperText={formik.touched.broughtType && formik.errors.broughtType}
              >
                <MenuItem value="personal">Personal</MenuItem>
                <MenuItem value="ambulance">Ambulance</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                select
                label="Police Report"
                fullWidth
                name="firReport"
                value={formik.values.firReport}
                onChange={formik.handleChange}
                error={formik.touched.firReport && Boolean(formik.errors.firReport)}
                helperText={formik.touched.firReport && formik.errors.firReport}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Grid>

            {formik.values.broughtType === "ambulance" && (
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Ambulance Number"
                    fullWidth
                    name="ambulanceNumber"
                    value={formik.values.ambulanceNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}   // to mark touched
                    error={formik.touched.ambulanceNumber && Boolean(formik.errors.ambulanceNumber)}
                    helperText={formik.touched.ambulanceNumber && formik.errors.ambulanceNumber}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Driver Name"
                    fullWidth
                    name="driverName"
                    value={formik.values.driverName}
                    onChange={(e) => {
                      let inputValue = e.target.value;
                      
                      // Capitalize first letter
                      if (inputValue.length > 0) {
                        inputValue = inputValue.charAt(0).toUpperCase() + inputValue.slice(1);
                      }
                      
                      formik.setFieldValue("driverName", inputValue);
                    }}
                    onBlur={formik.handleBlur}   // to mark touched
                    error={formik.touched.driverName && Boolean(formik.errors.driverName)}
                    helperText={formik.touched.driverName && formik.errors.driverName}
                  />
                </Grid>
              </>
            )}

            {formik.values.firReport === "yes" && (
              <Grid item xs={12}>
                <TextField
                  label="Upload FIR File"
                  fullWidth
                  name="firFile"
                  type="file"
                  onChange={(e) => {
                    formik.setFieldValue("firFile", e.currentTarget.files[0]);
                  }}
                  onBlur={() => formik.setFieldTouched("firFile", true)}  // manually set touched on blur
                  error={formik.touched.firFile && Boolean(formik.errors.firFile)}
                  helperText={formik.touched.firFile && formik.errors.firFile}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
          </Grid>

          {/* Submit button - not inside scroll */}
          <Box mt={3}>
            <FormModalActions
              onCancel={onClose}
              submitType="submit"
            />
          </Box>
        </form>
      </Box>
    </BasicModal>
  );
};

export default EmergencyAdmission;